import React, { useState, useEffect, useMemo } from "react";
import { IconButton, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";

import WorkFlowApprovalStepsHistory from "./WorkFlowApprovalStepsHistory";
import "./Sidebar.css";
import { AppRoutings } from "utility/enums/app-routings";
import Emitter from "utility/emitter";
import {
	ReqConversionToBidActionType,
	RequisitionActionType,
} from "utility/enums/requisition-status";
import { RequisitionStepHistoryEmitter } from "models/Requisition/RequisitionStepHistoryEmitter";
import SidebarMenu from "./SidebarMenu";
import ProcurementMethodSelection from "components/Requisition/RequisitionEntry/ProcurementMethodSelection";
import { BudgetAllocationStepHistory } from "components/BudgetManagement/BudgetAllocation/BudgetAllocationStepHistory";
import { BudgetAllocationStepHistoryEmitter } from "models/Budget/BudgetAllocationDetail";
import useMediaQuery from "@mui/material/useMediaQuery";
import { BudgetAllocationActionType } from "utility/enums/budget-allocation";
import {
	SHOW_WORKFLOW_HISTORY_ICON,
	SHOW_BUDGET_ALLOCATION_HISTORY_ICON,
	SHOW_HIDE_BUDGET_ALLOCATION_SIDEBAR,
	UPDATE_REQUISITION_LINE_PROC_METHOD,
	SET_ALLOCATION_HISTORY_ICON_STYLE,
	SHOW_SUPPLIER_DOCUMENT_HISTORY_ICON,
	SET_SUPPLIER_DOC_HISTORY_ICON_STYLE,
	SHOW_BID_HISTORY_ICON,
	SHOW_CONTRACT_HISTORY_ICON,
	SHOW_ITEM_WORKFLOW_HISTORY_ICON,
} from "utility/emitterEvents";
import { SupplierDocumentStepHistory } from "components/SupplyChainManagementPortal/SupplierDocumentUpload/SupplierDocumentStepHistory";
import { SupplierDocStepHistoryEmitter } from "models/VendorPortal/InvoiceUploadSummaryGridDetail";
import { ActionType } from "utility/enums/action-type";
import { RequisitionConversionToBidStepHistory } from "components/Requisition/RequisitionConversionToBid/RequisitionConversionToBidStepHistory";
import { BidStepHistoryEmitter } from "models/Bid/BidSummaryGridDetail";
import { RequisitionItemSelection } from "components/Requisition/RequisitionConversionToBid/RequisitionItemSelection";
import { BidActionType } from "utility/enums/bid-detail";
import { ContractStepHistoryEmitter } from "models/ContractManagement/ContractStepHistoryEmitter";
import ContractStepsHistory from "components/ContractManagement/NewContractEntry/ContractStepsHistory";
import { ContractActionType } from "utility/enums/contract-detail";
import { ContractItemSelection } from "components/ContractManagement/NewContractEntry/ContractItemSelection";
import { ItemActionType } from "utility/enums/item-details";
import { ItemStepHistoryEmitter } from "models/Item/itemStepHistoryEmitter";
import ItemWorkFlowApprovalStepsHistory from "./ItemWorkFlowApprovalStepsHistory";

const Sidebar: React.FC = () => {
	const location = useLocation();
	// use of useMediaQuery is required to manage styling for steps history icon, if budget allocation detail is loaded with step history list
	// Note: Breakpoints must be same as added in BudgetAllocation.css
	const theme = useTheme();
	const isTabletView = useMediaQuery(theme.breakpoints.between(899, 1199), {
		noSsr: true,
	});
	const isMobileView = useMediaQuery(theme.breakpoints.down(899), {
		noSsr: true,
	});
	const isDesktopView = useMediaQuery(theme.breakpoints.up(1199), {
		noSsr: true,
	});

	const [requisitionDetail, setRequisitionDetail] =
		useState<RequisitionStepHistoryEmitter>(
			new RequisitionStepHistoryEmitter()
		); // required to manage work flow step history detail
	const [contractDetail, setContractDetail] =
		useState<ContractStepHistoryEmitter>(new ContractStepHistoryEmitter()); // required to manage work flow step history detail
	const [bidDetail, setBidDetail] = useState<BidStepHistoryEmitter>(
		new BidStepHistoryEmitter()
	); // required to manage work flow step history detail
	const [itemDetail, setItemDetail] = useState<ItemStepHistoryEmitter>(
		new ItemStepHistoryEmitter()
	); // required to manage work flow step history detail
	const [procurementMethodWindowDetail, setProcurementMethodWindowDetail] =
		useState<{
			showProcurementMethodSelection: boolean | null;
			isEditMode: boolean | null;
			requisitionStepHistoryId: number | null;
		}>({
			showProcurementMethodSelection: null,
			isEditMode: null,
			requisitionStepHistoryId: null,
		}); // required to show/hide procurement method selection window
	const [budgetAllocationDetail, setBudgetAllocationDetail] =
		useState<BudgetAllocationStepHistoryEmitter>(
			new BudgetAllocationStepHistoryEmitter()
		); // required to manage budget allocation step history detail
	const [supplierDocDetail, setSupplierDocDetail] =
		useState<SupplierDocStepHistoryEmitter>(
			new SupplierDocStepHistoryEmitter()
		); // required to manage budget allocation step history detail
	const [budgetHistoryIconStyle, setBudgetHistoryIconStyle] = useState({
		leftPadding: 0,
		zIndex: 1299,
	});

	const updateBudgetHistoryIconStyle = (
		leftPadding: number,
		zIndex: number = 1299
	) => {
		if (
			budgetAllocationDetail.budgetAllocationId > 0 &&
			budgetAllocationDetail.budgetAllocationStepId > 0
		) {
			setBudgetHistoryIconStyle({ leftPadding: leftPadding, zIndex: zIndex });
		}
		// TODO: update logic for supplier doc upload page
		if (isSupplierDocumentUploadRoute)
			setBudgetHistoryIconStyle({ leftPadding: leftPadding, zIndex: zIndex });
	};

	const isBudgetAllocationRoute = useMemo(
		() =>
			location.pathname === AppRoutings.BudgetAllocationDetail ||
			location.pathname.includes(
				`/budget-allocation-detail/${BudgetAllocationActionType.Edit}`
			),
		[location.pathname]
	);

	const isSupplierDocumentUploadRoute = useMemo(
		() => location.pathname === AppRoutings.SupplierDocumentUpload,
		[location.pathname]
	);

	const isRequisitionEntry = useMemo(
		() =>
			location.pathname === AppRoutings.RequisitionEntry ||
			location.pathname.includes(
				`${AppRoutings.RequisitionEntry}/${RequisitionActionType.Edit}`
			) ||
			location.pathname.includes(
				`${AppRoutings.RequisitionEntry}/${RequisitionActionType.Delete}`
			) ||
			location.pathname === AppRoutings.ContractPOEntry ||
			location.pathname.includes(
				`${AppRoutings.ContractPOEntry}/${RequisitionActionType.Edit}`
			) ||
			location.pathname.includes(
				`${AppRoutings.ContractPOEntry}/${RequisitionActionType.Delete}`
			) ||
			location.pathname === AppRoutings.ContractVariationEntry ||
			location.pathname.includes(
				`${AppRoutings.ContractVariationEntry}/${RequisitionActionType.Edit}`
			) ||
			location.pathname.includes(
				`${AppRoutings.ContractVariationEntry}/${RequisitionActionType.Delete}`
			),
		[location.pathname]
	);

	const isContractEntry = useMemo(
		() =>
			location.pathname === AppRoutings.NewContractEntry ||
			location.pathname.includes(
				`${AppRoutings.NewContractEntry}/${ContractActionType.Edit}`
			) ||
			location.pathname.includes(
				`${AppRoutings.NewContractEntry}/${ContractActionType.Delete}`
			),
		[location.pathname]
	);

	const isBidDetail = useMemo(
		() =>
			location.pathname === AppRoutings.RequisitionConversionToBid ||
			location.pathname.includes(
				`requisition-conversion-to-bid/${ReqConversionToBidActionType.Convert}`
			) ||
			location.pathname.includes(`bid-entry/${BidActionType.Edit}`) ||
			location.pathname === AppRoutings.BidDetail,
		[location.pathname]
	);

	const isItemEntry = useMemo(
		() =>
			location.pathname === AppRoutings.ItemDetail ||
			location.pathname.includes(
				`${AppRoutings.ItemDetail}/${ItemActionType.Edit}`
			) ||
			location.pathname.includes(
				`${AppRoutings.ItemDetail}/${ItemActionType.Delete}`
			),
		[location.pathname]
	);

	const showHistorySteps = (updateHistoryIconStyle = true) => {
		document.body.classList.add("hide-label");
		document.body.classList.add("show-steps");
		if (isRequisitionEntry) document.body.classList.add("requisition-steps"); // required for requisition entry page
		removeIemSelectionClass();
		if (!(window.innerWidth >= 900)) {
			document.body.classList.remove("show-mobile-menu");
		}
		if (updateHistoryIconStyle)
			updateBudgetHistoryIconStyle(isMobileView ? 219 : 297);
	};

	const hideHistorySteps = () => {
		document.body.classList.remove("show-steps");
		document.body.classList.remove("requisition-steps"); // required for requisition entry page
		updateBudgetHistoryIconStyle(isMobileView ? 0 : 63);
	};

	const toggleHistorySteps = () => {
		if (document.body.classList.contains("show-steps")) {
			hideHistorySteps();
		} else {
			showHistorySteps();
		}
	};

	const innerSupplierDocDiv = document.body.querySelector(
		".supplier-doc-div"
	) as HTMLElement;
	// const supplierDoc = document.body.querySelector(
	// 	".supplier-doc-upload-item-selection"
	// ) as HTMLElement;
	const footerDiv = document.body.querySelector(".footer") as HTMLElement;

	const showItemSelection = () => {
		document.body.classList.add("hide-label");
		document.body.classList.add("show-item-selection");
		document.body.classList.remove("show-steps");
		document.body.classList.remove("requisition-steps"); // required for requisition entry page
		if (isRequisitionEntry)
			document.body.classList.add("requisition-item-selection"); // required for requisition entry page
		if (isBudgetAllocationRoute)
			document.body.classList.add("allocation-item-selection");
		if (isSupplierDocumentUploadRoute) {
			document.body.classList.add("supplier-doc-upload-item-selection");
			if (innerSupplierDocDiv) {
				innerSupplierDocDiv.style.width = "50%";
				innerSupplierDocDiv.style.marginLeft = "1020px";
				innerSupplierDocDiv.style.paddingRight = "110px";
			}
			if (footerDiv) {
				footerDiv.style.width = "50%";
				footerDiv.style.marginLeft = "950px";
			}
		}
		if (!(window.innerWidth >= 900)) {
			document.body.classList.remove("show-mobile-menu");
		}
		updateBudgetHistoryIconStyle(
			isDesktopView ? 1083 : isTabletView ? 863 : 0,
			isMobileView ? 2 : 1299
		);
	};

	const removeIemSelectionClass = () => {
		document.body.classList.remove("show-item-selection");
		document.body.classList.remove("allocation-item-selection");
		if (innerSupplierDocDiv) {
			innerSupplierDocDiv.style.width = "100%";
			innerSupplierDocDiv.style.marginLeft = "0px";
			innerSupplierDocDiv.style.paddingRight = "0px";
		}
		if (footerDiv) {
			footerDiv.style.width = "100%";
			footerDiv.style.marginLeft = "0px";
		}
		document.body.classList.remove("supplier-doc-upload-item-selection");
		document.body.classList.remove("requisition-item-selection"); // required for requisition entry page
	};

	const hideItemSelection = (updateHistoryIconStyle = true) => {
		removeIemSelectionClass();
		if (updateHistoryIconStyle)
			updateBudgetHistoryIconStyle(
				isMobileView ? 0 : 63,
				isMobileView ? 2 : 1299
			);
	};

	const toggleItemSelection = () => {
		if (document.body.classList.contains("show-item-selection")) {
			hideItemSelection();
		} else {
			showItemSelection();
		}
	};

	useEffect(() => {
		// show/hide work flow history icon
		Emitter.off(SHOW_WORKFLOW_HISTORY_ICON, {} as any);
		Emitter.on(
			SHOW_WORKFLOW_HISTORY_ICON,
			(model: RequisitionStepHistoryEmitter) => {
				setRequisitionDetail(model);
				if (
					model.requisitionId > -1 &&
					!document.body.classList.contains("show-steps")
				) {
					showHistorySteps();
				} else {
					hideHistorySteps();
				}
			}
		);
		Emitter.off(SHOW_BID_HISTORY_ICON, {} as any);
		Emitter.on(SHOW_BID_HISTORY_ICON, (model: BidStepHistoryEmitter) => {
			setBidDetail(model);
			if (
				model.bidId > 0 &&
				model.bidStepId > 0 &&
				!document.body.classList.contains("show-steps")
			) {
				hideItemSelection(false);
				showHistorySteps();
				setBudgetHistoryIconStyle({
					leftPadding: isMobileView ? 219 : 297,
					zIndex:
						isMobileView &&
						document.body.classList.contains("show-item-selection")
							? 2
							: 1299,
				});
			} else if (model.bidId > 0 && model.bidStepId > 0) {
				// added for directly opening steps instead of item selection
				hideItemSelection(false);
				showHistorySteps();
			} else {
				hideHistorySteps();
				if (model.isCsdRfq) hideItemSelection(false);
				else {
					showItemSelection();
				}
			}
		});
		// show/hide allocation step history icon
		Emitter.off(SHOW_BUDGET_ALLOCATION_HISTORY_ICON, {} as any);
		Emitter.on(
			SHOW_BUDGET_ALLOCATION_HISTORY_ICON,
			(model: BudgetAllocationStepHistoryEmitter) => {
				setBudgetAllocationDetail(model);
				if (
					model.budgetAllocationId > -1 &&
					model.budgetAllocationStepId > -1 &&
					!document.body.classList.contains("show-steps")
				) {
					hideItemSelection(false);
					showHistorySteps(false);
					setBudgetHistoryIconStyle({
						leftPadding: isMobileView ? 219 : 297,
						zIndex:
							isMobileView &&
							document.body.classList.contains("show-item-selection")
								? 2
								: 1299,
					});
				} else {
					hideHistorySteps();
					showItemSelection();
				}
			}
		);
		Emitter.off(SHOW_SUPPLIER_DOCUMENT_HISTORY_ICON, {} as any);
		Emitter.on(
			SHOW_SUPPLIER_DOCUMENT_HISTORY_ICON,
			(model: SupplierDocStepHistoryEmitter) => {
				setSupplierDocDetail(model);
				// Note: if condition is required to expand step history panel on load
				// if (
				// 	model.supplierDocId > -1 &&
				// 	!document.body.classList.contains("show-steps")
				// ) {
				// hideItemSelection(false);
				// showHistorySteps(false);
				// setBudgetHistoryIconStyle({
				// 	leftPadding: isMobileView ? 219 : 297,
				// 	zIndex:
				// 		isMobileView &&
				// 		document.body.classList.contains("show-item-selection")
				// 			? 2
				// 			: 1299,
				// });
				// } else {
				hideHistorySteps();
				showItemSelection();
				// }
			}
		);
		// show/hide contract work flow history icon
		Emitter.off(SHOW_CONTRACT_HISTORY_ICON, {} as any);
		Emitter.on(
			SHOW_CONTRACT_HISTORY_ICON,
			(model: ContractStepHistoryEmitter) => {
				setContractDetail(model);
				if (
					model.contractId > -1 &&
					!document.body.classList.contains("show-steps")
				) {
					showHistorySteps();
				} else {
					hideHistorySteps();
				}
			}
		);
		// show/hide item work flow history icon
		Emitter.off(SHOW_ITEM_WORKFLOW_HISTORY_ICON, {} as any);
		Emitter.on(
			SHOW_ITEM_WORKFLOW_HISTORY_ICON,
			(model: ItemStepHistoryEmitter) => {
				setItemDetail(model);
				if (
					model.itemId > -1 &&
					!document.body.classList.contains("show-steps")
				) {
					showHistorySteps();
				} else {
					hideHistorySteps();
				}
			}
		);
		return () => {
			Emitter.off(SHOW_WORKFLOW_HISTORY_ICON, undefined);
			Emitter.off(SHOW_BID_HISTORY_ICON, undefined);
			Emitter.off(SHOW_BUDGET_ALLOCATION_HISTORY_ICON, undefined);
			Emitter.off(SHOW_SUPPLIER_DOCUMENT_HISTORY_ICON, undefined);
			Emitter.off(SHOW_CONTRACT_HISTORY_ICON, undefined);
			Emitter.off(SHOW_ITEM_WORKFLOW_HISTORY_ICON, undefined);
			hideItemSelection();
			hideHistorySteps();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// useEffect(() => {
	// 	if (isBidDetail) showItemSelection();
	// 	else hideItemSelection();
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [location.pathname]);

	useEffect(() => {
		// show/hide budget allocation sidebar
		Emitter.on(
			SHOW_HIDE_BUDGET_ALLOCATION_SIDEBAR,
			(payload: { hideSidebar?: boolean }) => {
				if (payload.hideSidebar) hideItemSelection();
				else showItemSelection();
			}
		);
		Emitter.on(
			SET_ALLOCATION_HISTORY_ICON_STYLE,
			(payload: { leftPadding: number; zIndex?: number }) => {
				if (!document.getElementsByClassName("allocation-history-icon").length)
					return;
				updateBudgetHistoryIconStyle(payload.leftPadding, payload.zIndex);
			}
		);
		return () => {
			Emitter.off(SHOW_HIDE_BUDGET_ALLOCATION_SIDEBAR, undefined);
			Emitter.off(SET_ALLOCATION_HISTORY_ICON_STYLE, undefined);
		};
		// budgetAllocationDetail- added as a dependency to update history icon position when sidebar is opened
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [budgetAllocationDetail]);

	useEffect(() => {
		Emitter.on(
			SET_SUPPLIER_DOC_HISTORY_ICON_STYLE,
			(payload: { leftPadding: number; zIndex?: number }) => {
				if (
					!document.getElementsByClassName("supplier-doc-history-icon").length
				)
					return;
				updateBudgetHistoryIconStyle(payload.leftPadding, payload.zIndex);
			}
		);
		return () => {
			Emitter.off(SET_SUPPLIER_DOC_HISTORY_ICON_STYLE, undefined);
		};
		// supplierDocDetail - added as a dependency to update history icon position when sidebar is opened
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supplierDocDetail]);

	// handle history step selection and manage procurement method selection window
	const openProcurementMethodSelectionWindow = (
		showProcurementMethodSelection: boolean,
		editableProcurementMethodSelection: boolean,
		requisitionStepHistoryId: number
	) => {
		Emitter.emit(UPDATE_REQUISITION_LINE_PROC_METHOD, {});
		setProcurementMethodWindowDetail({
			showProcurementMethodSelection: null,
			isEditMode: null,
			requisitionStepHistoryId: null,
		});
		setTimeout(() => {
			setProcurementMethodWindowDetail({
				showProcurementMethodSelection: showProcurementMethodSelection,
				isEditMode: editableProcurementMethodSelection,
				requisitionStepHistoryId: requisitionStepHistoryId,
			});
			if (showProcurementMethodSelection) {
				hideHistorySteps();
				showItemSelection();
			}
		}, 0);
	};

	const getBudgetHistoryIconStyle = () => {
		return {
			left: `${budgetHistoryIconStyle.leftPadding}px !important`,
			zIndex: `${budgetHistoryIconStyle.zIndex} !important`,
		};
	};

	useEffect(() => {
		updateBudgetHistoryIconStyle(
			isTabletView
				? document.body.classList.contains("show-steps")
					? 297
					: document.body.classList.contains("show-item-selection")
					? 863
					: !document.body.classList.contains("hide-label")
					? 219
					: 63
				: isDesktopView
				? document.body.classList.contains("show-steps")
					? 297
					: document.body.classList.contains("show-item-selection")
					? 1083
					: !document.body.classList.contains("hide-label")
					? 219
					: 63
				: isMobileView
				? document.body.classList.contains("show-steps")
					? 219
					: 0
				: 0,
			isMobileView && document.body.classList.contains("show-item-selection")
				? 2
				: 1299
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMobileView, isTabletView, isDesktopView]);

	return (
		<div className="sidebar-outer">
			{isRequisitionEntry && requisitionDetail.requisitionId > 0 && (
				<>
					<IconButton
						className="history-icon"
						onClick={toggleHistorySteps}
					></IconButton>
					<WorkFlowApprovalStepsHistory
						requisitionDetail={requisitionDetail}
						openProcurementMethodSelectionWindow={
							openProcurementMethodSelectionWindow
						}
					/>
					{/* Procurement Method Selection Window */}
					{(procurementMethodWindowDetail.showProcurementMethodSelection ||
						procurementMethodWindowDetail.isEditMode) &&
						requisitionDetail.requisitionId > 0 && (
							<>
								<IconButton
									className="item-selection-icon"
									onClick={toggleItemSelection}
								></IconButton>
								<ProcurementMethodSelection
									requisitionId={requisitionDetail.requisitionId}
									requisitionStepHistoryId={Number(
										procurementMethodWindowDetail.requisitionStepHistoryId
									)}
									isEditMode={Boolean(procurementMethodWindowDetail.isEditMode)}
									isViewMode={requisitionDetail.actionType === ActionType.View}
								/>
							</>
						)}
				</>
			)}
			{isBidDetail && (
				<>
					{!bidDetail.isCsdRfq && (
						<>
							<IconButton
								className="item-selection-icon"
								onClick={toggleItemSelection}
							></IconButton>
							<RequisitionItemSelection
								isViewMode={bidDetail.bidStepId > 0}
								isSupplierBidding={bidDetail.isSupplierBidding}
							/>
						</>
					)}
					{bidDetail.bidId > 0 && bidDetail.bidStepId > 0 && (
						<>
							<IconButton
								className="history-icon"
								onClick={toggleHistorySteps}
							></IconButton>
							<RequisitionConversionToBidStepHistory bidDetail={bidDetail} />
						</>
					)}
				</>
			)}
			{isBudgetAllocationRoute && (
				<>
					<IconButton
						className="item-selection-icon budget-allocation-panel"
						onClick={toggleItemSelection}
					></IconButton>
					{budgetAllocationDetail.budgetAllocationId > 0 &&
						budgetAllocationDetail.budgetAllocationStepId > 0 && (
							<>
								<IconButton
									className="allocation-history-icon"
									onClick={toggleHistorySteps}
									sx={getBudgetHistoryIconStyle()}
								></IconButton>
								<BudgetAllocationStepHistory
									budgetAllocationDetail={budgetAllocationDetail}
								/>
							</>
						)}
				</>
			)}
			{isSupplierDocumentUploadRoute && (
				<>
					<IconButton
						className="item-selection-icon supplier-doc-upload-panel"
						onClick={toggleItemSelection}
					></IconButton>
					{supplierDocDetail.supplierDocId > 0 && (
						<>
							<IconButton
								className="supplier-doc-history-icon"
								onClick={toggleHistorySteps}
								sx={getBudgetHistoryIconStyle()}
							></IconButton>
							<SupplierDocumentStepHistory />
						</>
					)}
				</>
			)}
			{isContractEntry && contractDetail.contractStepId < 0 && (
				<>
					<IconButton
						className="item-selection-icon"
						onClick={toggleItemSelection}
					></IconButton>
					<ContractItemSelection />
				</>
			)}
			{isContractEntry && contractDetail.contractId > 0 && (
				<>
					<IconButton
						className="history-icon"
						onClick={toggleHistorySteps}
					></IconButton>
					<ContractStepsHistory contractDetail={contractDetail} />
				</>
			)}
			{isItemEntry && itemDetail.itemId > 0 && (
				<>
					<IconButton
						className="history-icon"
						onClick={toggleHistorySteps}
					></IconButton>
					<ItemWorkFlowApprovalStepsHistory itemDetail={itemDetail} />
					{/* Procurement Method Selection Window */}
					{(procurementMethodWindowDetail.showProcurementMethodSelection ||
						procurementMethodWindowDetail.isEditMode) &&
						requisitionDetail.requisitionId > 0 && (
							<>
								<IconButton
									className="item-selection-icon"
									onClick={toggleItemSelection}
								></IconButton>
								<ProcurementMethodSelection
									requisitionId={requisitionDetail.requisitionId}
									requisitionStepHistoryId={Number(
										procurementMethodWindowDetail.requisitionStepHistoryId
									)}
									isEditMode={Boolean(procurementMethodWindowDetail.isEditMode)}
									isViewMode={requisitionDetail.actionType === ActionType.View}
								/>
							</>
						)}
				</>
			)}
			<SidebarMenu />
		</div>
	);
};

export default Sidebar;
